import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle2, XCircle, CreditCard } from 'lucide-react';
import { getStripeErrorMessage } from '@/lib/stripeErrorMessages';
import { type AutoChargePayment } from '@/hooks/finance/useAutoChargePayments';

type ModalState = 'confirm' | 'processing' | 'success' | 'error';

// Interface genérica para suportar ambos os contextos (AutoChargeTab e PaymentsTab)
export interface ChargePaymentData {
  id: string;
  amount: number;
  paid_amount?: number;
  installment_number?: number;
  deal_name: string;
  client_name: string;
}

interface ChargePaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: AutoChargePayment | ChargePaymentData | null;
  onCharge: (paymentId: string) => Promise<{ success: boolean; error?: string; amount_charged?: number }>;
  isCharging: boolean;
}

function formatCurrency(value: number, currency: string = 'USD') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(value);

}

// Helper to check if payment is AutoChargePayment type
function isAutoChargePayment(payment: AutoChargePayment | ChargePaymentData): payment is AutoChargePayment {
  return 'deal' in payment && payment.deal !== undefined;
}

export function ChargePaymentModal({
  open,
  onOpenChange,
  payment,
  onCharge,
  isCharging
}: ChargePaymentModalProps) {
  const [state, setState] = useState<ModalState>('confirm');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [chargedAmount, setChargedAmount] = useState<number>(0);
  
  // Ref para evitar reset durante processamento
  const isProcessingRef = useRef(false);

  // Reset state when modal opens - mas NÃO durante processamento
  useEffect(() => {
    if (open && !isProcessingRef.current) {
      setState('confirm');
      setErrorMessage('');
      setChargedAmount(0);
    }
  }, [open]);

  if (!payment) return null;

  const balance = payment.amount - (payment.paid_amount || 0);
  
  // Handle both payment types
  let clientName: string;
  let dealName: string;
  
  if (isAutoChargePayment(payment)) {
    const contact = payment.deal.contact;
    clientName = `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Cliente';
    dealName = payment.deal.name;
  } else {
    clientName = payment.client_name || 'Cliente';
    dealName = payment.deal_name || 'Deal';
  }

  const handleCharge = async () => {
    isProcessingRef.current = true;
    setState('processing');
    
    try {
      const result = await onCharge(payment.id);
      
      if (result.success) {
        setChargedAmount(result.amount_charged || balance);
        setState('success');
      } else {
        setErrorMessage(getStripeErrorMessage(result.error, result.error));
        setState('error');
      }
    } catch (error: any) {
      setErrorMessage(getStripeErrorMessage(error?.message, error?.message || 'Erro desconhecido'));
      setState('error');
    } finally {
      isProcessingRef.current = false;
    }
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={state === 'processing' ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        {/* Confirm State */}
        {state === 'confirm' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Cobrar Agora
              </DialogTitle>
              <DialogDescription className="pt-2">
                Deseja processar a cobrança do cartão cadastrado?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Cliente:</span>
                <span className="font-medium">{clientName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Deal:</span>
                <span className="font-medium">{dealName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Parcela:</span>
                <span className="font-medium">#{payment.installment_number || '-'}</span>
              </div>
              <div className="flex justify-between items-center border-t pt-3">
                <span className="text-muted-foreground">Valor a cobrar:</span>
                <span className="font-bold text-lg text-primary">
                  {formatCurrency(balance)}
                </span>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button onClick={handleCharge} disabled={isCharging}>
                Confirmar Cobrança
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Processing State */}
        {state === 'processing' && (
          <div className="py-12 flex flex-col items-center justify-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            <p className="text-lg font-medium">Processando cobrança...</p>
            <p className="text-sm text-muted-foreground">
              Aguarde, estamos processando o pagamento no Stripe.
            </p>
          </div>
        )}

        {/* Success State */}
        {state === 'success' && (
          <>
            <div className="py-8 flex flex-col items-center justify-center gap-4">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle2 className="h-12 w-12 text-green-600" />
              </div>
              <p className="text-lg font-medium text-green-600">Cobrança Realizada!</p>
              <div className="text-center space-y-1">
                <p className="text-2xl font-bold">
                  {formatCurrency(chargedAmount)}
                </p>
                <p className="text-sm text-muted-foreground">
                  cobrado do cartão de {clientName}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleClose} className="w-full">
                Fechar
              </Button>
            </DialogFooter>
          </>
        )}

        {/* Error State */}
        {state === 'error' && (
          <>
            <div className="py-8 flex flex-col items-center justify-center gap-4">
              <div className="rounded-full bg-destructive/10 p-3">
                <XCircle className="h-12 w-12 text-destructive" />
              </div>
              <p className="text-lg font-medium text-destructive">Cobrança Falhou</p>
              <div className="text-center space-y-2 max-w-[300px]">
                <p className="text-sm font-medium bg-destructive/10 text-destructive px-4 py-2 rounded-md">
                  {errorMessage}
                </p>
                <p className="text-xs text-muted-foreground">
                  Verifique os dados do cartão ou entre em contato com o cliente.
                </p>
              </div>
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button variant="outline" onClick={() => setState('confirm')} className="w-full">
                Tentar Novamente
              </Button>
              <Button variant="ghost" onClick={handleClose} className="w-full">
                Fechar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
